import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'

import { useAuth } from '@/contexts/auth-context'
import { useWorkflow } from '@/context/workflow-context-simple'
import { useWorkflowApi } from '@/hooks/use-workflow-api'
import { useToast } from '@/hooks/use-toast'
import { useTranslation } from '@/hooks/use-translation'

// The preference is user-global, but the hub mounts SEVERAL isolated WorkflowProviders — a root one plus one
// per request tab (see *-sub-tab-content) — each with its own reducer state. A dispatch reaches only the
// provider the modal sits under, so a per-tab grid's badges (and its mark-read gate) kept the old value until
// that provider re-fetched configuration on a page refresh. Holding the optimistic value in this module-level
// store, which every hook instance subscribes to, updates every provider's consumers at once. It resets on a
// full reload — and, because it would otherwise shadow a config refresh, whenever the signed-in profile
// changes (see the hook below), so a token swap to another user never leaves them on the previous user's
// optimistic value. Until the user toggles it is null, so each provider's own persisted config value is the
// source of truth.
let optimisticEnabled: boolean | null = null
// Whose override the value above belongs to. Compared at the module level so a genuine user switch clears
// it exactly once — a bare per-instance effect can't, because React runs a mount effect once regardless of
// its deps, so every new per-tab hook instance would otherwise wipe a live toggle.
let optimisticProfileId: string | null = null
const optimisticListeners = new Set<() => void>()
function setOptimisticEnabled(value: boolean | null, profileId: string | null) {
  optimisticEnabled = value
  optimisticProfileId = profileId
  optimisticListeners.forEach((notify) => notify())
}
// Drop the override only when the signed-in profile is genuinely different from the one it was set for.
// A re-mount under the same user leaves it untouched (the profile ids match); a user switch clears it.
function clearOptimisticIfUserChanged(profileId: string | null) {
  if (optimisticEnabled === null || optimisticProfileId === profileId) return
  optimisticEnabled = null
  optimisticProfileId = null
  optimisticListeners.forEach((notify) => notify())
}
function subscribeOptimistic(notify: () => void) {
  optimisticListeners.add(notify)
  return () => { optimisticListeners.delete(notify) }
}
const getOptimisticSnapshot = () => optimisticEnabled

/**
 * Whether this user wants message notifications — unread counts on rows and tabs and important flags
 * (hover previews are NOT gated by this; they stay on) — and, critically, whether opening a
 * conversation marks it read.
 *
 * **This is the gate ALP-154 calls out as safety-critical.** Reading and sending are gated
 * differently and that asymmetry is the point: send always advances the sender's own watermark by
 * one ("you cannot be unread on your own message" is an invariant), and only mark-read-on-open is
 * gated. So an off period leaves prior unread untouched and turning it back on replays nothing.
 * Route every consumer through this hook — reading the stored value directly is exactly how a client
 * escalation gets silently marked read.
 *
 * **Persisted on `UserViewState`, and deliberately user-global.** Those rows are per entity tab, but
 * this preference is not: the modal tells the user it applies on every tab and every client, so the
 * backend fans the value out to every row on their partition. Saving sends
 * `messageNotificationsEnabled` **only** when it changes — omitted means "leave alone", so an
 * ordinary column-width or page-size save can never switch someone's notifications off.
 */
export function useMessageNotifications() {
  const { state, currentView, setMessageNotificationsEnabled: setShared } = useWorkflow()
  const { profileId } = useAuth()
  const { saveViewState } = useWorkflowApi()
  const { toast } = useToast()
  const { t } = useTranslation()
  const [isSaving, setIsSaving] = useState(false)

  // Clear the cross-provider override when the signed-in profile genuinely changes (a token swap to a
  // different user), so they don't read the previous user's optimistic value — which would otherwise
  // outlast the config refresh. The module-level guard makes this a no-op on a mere re-mount under the same
  // user, and a same-user token refresh keeps the override.
  useEffect(() => { clearOptimisticIfUserChanged(profileId) }, [profileId])

  // The optimistic override lives in the module-level store above, so it crosses every WorkflowProvider —
  // the modal, the menu label, and every per-tab grid's badge gate settle together. Null until the user
  // toggles, so each provider's own persisted config value (`state.messageNotificationsEnabled`) is the
  // initial source of truth.
  const optimistic = useSyncExternalStore(subscribeOptimistic, getOptimisticSnapshot, getOptimisticSnapshot)
  const enabled = optimistic ?? state.messageNotificationsEnabled ?? true

  const setEnabled = useCallback(
    async (next: boolean): Promise<boolean> => {
      if (next === enabled) return true

      // Broadcast to every provider's consumers immediately, and keep this provider's own reducer state in
      // step for the reducer's config-merge fallback.
      setOptimisticEnabled(next, profileId)
      setShared(next)
      setIsSaving(true)
      try {
        // Minimal payload on purpose, tagged isMessageNotificationsUpdate so the server updates ONLY the
        // preference and leaves the saved view (stage configs, filters, column widths, page size) intact.
        // Sending the current grid state here would let a notifications toggle quietly commit whatever
        // unsaved state the user happened to have.
        await saveViewState({
          id: currentView?.viewSettingsId,
          title: currentView?.title,
          isMessageNotificationsUpdate: true,
          messageNotificationsEnabled: next,
        } as any)
        return true
      } catch (error) {
        // Roll the optimistic value back — the server still has the old one.
        setOptimisticEnabled(!next, profileId)
        setShared(!next)
        toast({
          title: t('messageNotificationsSaveError') || 'Could not save your preference',
          description: error instanceof Error ? error.message : (t('messageNotificationsSaveErrorRetry') || 'Please try again.'),
          variant: 'destructive',
        })
        return false
      } finally {
        setIsSaving(false)
      }
    },
    [enabled, saveViewState, currentView, toast, setShared, profileId]
  )

  return { messageNotificationsEnabled: enabled, setMessageNotificationsEnabled: setEnabled, isSaving }
}
